/**
 * Phase 3 Custom Nodes
 * React Flow custom nodes for AI-powered diagram types:
 * - System Context (C4 Level 1)
 * - Threat Model (Security)
 * - Service Mesh
 * - SRE Runbook
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';

// ============= Color Schemes =============

// System Context & C4 Colors
const C4_COLORS = {
  person: { bg: '#08427b', border: '#073b6c', text: '#ffffff' },
  softwareSystem: { bg: '#1168bd', border: '#0e5aa7', text: '#ffffff' },
  externalSystem: { bg: '#999999', border: '#8a8a8a', text: '#ffffff' },
  container: { bg: '#438dd5', border: '#3c7fc0', text: '#ffffff' },
};

// Security/Threat Model Colors
const SECURITY_COLORS = {
  process: { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
  dataStore: { bg: '#fff3e0', border: '#f57c00', text: '#e65100' },
  externalEntity: { bg: '#f3e5f5', border: '#7b1fa2', text: '#4a148c' },
  threat: { bg: '#ffebee', border: '#c62828', text: '#b71c1c' },
  control: { bg: '#e8f5e9', border: '#2e7d32', text: '#1b5e20' },
  trustBoundary: { bg: '#fce4ec', border: '#c2185b', text: '#880e4f' },
};

// Service Mesh Colors
const MESH_COLORS = {
  service: { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
  gateway: { bg: '#fff3e0', border: '#f57c00', text: '#e65100' },
  loadBalancer: { bg: '#e8f5e9', border: '#388e3c', text: '#1b5e20' },
  sidecar: { bg: '#f3e5f5', border: '#7b1fa2', text: '#4a148c' },
  database: { bg: '#fce4ec', border: '#c2185b', text: '#880e4f' },
  cache: { bg: '#e0f7fa', border: '#0097a7', text: '#006064' },
  queue: { bg: '#fff8e1', border: '#ffa000', text: '#ff6f00' },
  external: { bg: '#efebe9', border: '#5d4037', text: '#3e2723' },
};

// SRE Runbook Colors
const RUNBOOK_COLORS = {
  trigger: { bg: '#ffcdd2', border: '#c62828', text: '#b71c1c' },
  decision: { bg: '#fff9c4', border: '#f9a825', text: '#f57f17' },
  action: { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
  automation: { bg: '#e8f5e9', border: '#388e3c', text: '#1b5e20' },
  escalation: { bg: '#f3e5f5', border: '#7b1fa2', text: '#4a148c' },
  resolution: { bg: '#c8e6c9', border: '#2e7d32', text: '#1b5e20' },
};

// ============= Icons =============

const PersonIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const SystemIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const ThreatIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const ServiceIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const AlertIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const RobotIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

// ============= C4/System Context Nodes =============

interface PersonNodeData {
  label: string;
  description?: string;
  [key: string]: unknown;
}

export const PersonNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as PersonNodeData;
  const colors = C4_COLORS.person;

  return (
    <div
      className={`flex flex-col items-center p-4 min-w-[120px] transition-all duration-200 ${
        selected ? 'ring-2 ring-blue-400 ring-offset-2' : ''
      }`}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-400" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400" />

      {/* Person circle */}
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-2"
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        <PersonIcon />
      </div>

      {/* Label */}
      <div className="text-center">
        <div className="font-semibold text-sm" style={{ color: colors.bg }}>
          {nodeData.label}
        </div>
        {nodeData.description && (
          <div className="text-xs text-gray-500 mt-1 max-w-[150px]">
            {nodeData.description}
          </div>
        )}
      </div>
    </div>
  );
});
PersonNode.displayName = 'PersonNode';

export const SoftwareSystemNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as PersonNodeData;
  const colors = C4_COLORS.softwareSystem;

  return (
    <div
      className={`px-4 py-3 min-w-[180px] rounded-lg border-2 transition-all duration-200 ${
        selected ? 'ring-2 ring-blue-400 ring-offset-2 shadow-lg' : 'shadow-md'
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-white" />
      <Handle type="source" position={Position.Bottom} className="!bg-white" />

      <div className="flex items-center gap-2 mb-1">
        <SystemIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
      {nodeData.description && (
        <div className="text-xs opacity-80" style={{ color: colors.text }}>
          {nodeData.description}
        </div>
      )}
    </div>
  );
});
SoftwareSystemNode.displayName = 'SoftwareSystemNode';

export const ExternalSystemNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as PersonNodeData;
  const colors = C4_COLORS.externalSystem;

  return (
    <div
      className={`px-4 py-3 min-w-[160px] rounded-lg border-2 border-dashed transition-all duration-200 ${
        selected ? 'ring-2 ring-gray-400 ring-offset-2 shadow-lg' : 'shadow-md'
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-white" />
      <Handle type="source" position={Position.Bottom} className="!bg-white" />

      <div className="font-semibold text-sm" style={{ color: colors.text }}>
        {nodeData.label}
      </div>
      <div className="text-xs opacity-80" style={{ color: colors.text }}>
        [External System]
      </div>
    </div>
  );
});
ExternalSystemNode.displayName = 'ExternalSystemNode';

// ============= DDD/Event Storming Nodes =============

// ============= Security/Threat Model Nodes =============

interface ThreatNodeData {
  label: string;
  description?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  category?: string;
  [key: string]: unknown;
}

export const ThreatNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as ThreatNodeData;
  const colors = SECURITY_COLORS.threat;

  const severityColors = {
    critical: '#c62828',
    high: '#e65100',
    medium: '#f9a825',
    low: '#2e7d32',
  };

  return (
    <div
      className={`px-3 py-2 min-w-[150px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-red-400 ring-offset-2' : ''
      }`}
      style={{
        backgroundColor: colors.bg,
        borderColor: nodeData.severity ? severityColors[nodeData.severity] : colors.border,
      }}
    >
      <Handle type="target" position={Position.Top} className="!bg-red-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-red-500" />

      <div className="flex items-center gap-2">
        <ThreatIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
      {nodeData.severity && (
        <div
          className="text-xs mt-1 px-2 py-0.5 rounded-full inline-block text-white"
          style={{ backgroundColor: severityColors[nodeData.severity] }}
        >
          {nodeData.severity}
        </div>
      )}
    </div>
  );
});
ThreatNode.displayName = 'ThreatNode';

export const ControlNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as ThreatNodeData;
  const colors = SECURITY_COLORS.control;

  return (
    <div
      className={`px-3 py-2 min-w-[140px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-green-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-green-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-green-500" />

      <div className="flex items-center gap-2">
        <ShieldIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
      {nodeData.description && (
        <div className="text-xs mt-1" style={{ color: colors.text }}>
          {nodeData.description}
        </div>
      )}
    </div>
  );
});
ControlNode.displayName = 'ControlNode';

export const TrustBoundaryNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as ThreatNodeData;
  const colors = SECURITY_COLORS.trustBoundary;

  return (
    <div
      className={`px-4 py-3 min-w-[200px] rounded-lg border-2 border-dashed shadow-sm transition-all duration-200 ${
        selected ? 'ring-2 ring-pink-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-pink-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-pink-500" />

      <div className="font-semibold text-sm flex items-center gap-2" style={{ color: colors.text }}>
        <span>🔒</span>
        {nodeData.label}
      </div>
      <div className="text-xs mt-1 opacity-70" style={{ color: colors.text }}>
        Trust Boundary
      </div>
    </div>
  );
});
TrustBoundaryNode.displayName = 'TrustBoundaryNode';

// ============= Service Mesh Nodes =============

interface ServiceMeshNodeData {
  label: string;
  description?: string;
  port?: number;
  protocol?: string;
  replicas?: number;
  [key: string]: unknown;
}

export const MeshServiceNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as ServiceMeshNodeData;
  const colors = MESH_COLORS.service;

  return (
    <div
      className={`px-3 py-2 min-w-[150px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-blue-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-blue-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-blue-500" />
      <Handle type="source" position={Position.Right} id="right" className="!bg-blue-500" />

      <div className="flex items-center gap-2">
        <ServiceIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
      <div className="flex gap-2 mt-1 text-xs" style={{ color: colors.text }}>
        {nodeData.port && <span>:{nodeData.port}</span>}
        {nodeData.protocol && <span>{nodeData.protocol}</span>}
        {nodeData.replicas && <span>x{nodeData.replicas}</span>}
      </div>
    </div>
  );
});
MeshServiceNode.displayName = 'MeshServiceNode';

export const GatewayNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as ServiceMeshNodeData;
  const colors = MESH_COLORS.gateway;

  return (
    <div
      className={`px-4 py-3 min-w-[160px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-orange-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-orange-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-orange-500" />

      <div className="font-semibold text-sm flex items-center gap-2" style={{ color: colors.text }}>
        <span>🚪</span>
        {nodeData.label}
      </div>
      <div className="text-xs mt-1 opacity-70" style={{ color: colors.text }}>
        API Gateway
      </div>
    </div>
  );
});
GatewayNode.displayName = 'GatewayNode';

export const LoadBalancerNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as ServiceMeshNodeData;
  const colors = MESH_COLORS.loadBalancer;

  return (
    <div
      className={`px-3 py-2 min-w-[140px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-green-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-green-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-green-500" />

      <div className="font-semibold text-sm flex items-center gap-2" style={{ color: colors.text }}>
        <span>⚖️</span>
        {nodeData.label}
      </div>
    </div>
  );
});
LoadBalancerNode.displayName = 'LoadBalancerNode';

// ============= SRE Runbook Nodes =============

interface RunbookNodeData {
  label: string;
  description?: string;
  owner?: string;
  estimatedTime?: string;
  automated?: boolean;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  [key: string]: unknown;
}

export const TriggerNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as RunbookNodeData;
  const colors = RUNBOOK_COLORS.trigger;

  return (
    <div
      className={`px-3 py-2 min-w-[150px] rounded-full border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-red-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="source" position={Position.Bottom} className="!bg-red-500" />

      <div className="flex items-center gap-2 justify-center">
        <AlertIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
    </div>
  );
});
TriggerNode.displayName = 'TriggerNode';

export const DecisionNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as RunbookNodeData;
  const colors = RUNBOOK_COLORS.decision;

  return (
    <div
      className={`px-4 py-3 min-w-[120px] shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-yellow-400 ring-offset-2' : ''
      }`}
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.border,
        clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
        borderWidth: 2,
        borderStyle: 'solid',
      }}
    >
      <Handle type="target" position={Position.Top} className="!bg-yellow-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-yellow-500" />
      <Handle type="source" position={Position.Right} id="no" className="!bg-yellow-500" />

      <div className="text-center font-semibold text-sm" style={{ color: colors.text }}>
        {nodeData.label}
      </div>
    </div>
  );
});
DecisionNode.displayName = 'DecisionNode';

export const ActionNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as RunbookNodeData;
  const colors = RUNBOOK_COLORS.action;

  return (
    <div
      className={`px-3 py-2 min-w-[160px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-blue-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-blue-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500" />

      <div className="font-semibold text-sm" style={{ color: colors.text }}>
        {nodeData.label}
      </div>
      {nodeData.owner && (
        <div className="text-xs mt-1 opacity-70" style={{ color: colors.text }}>
          👤 {nodeData.owner}
        </div>
      )}
      {nodeData.estimatedTime && (
        <div className="text-xs opacity-70" style={{ color: colors.text }}>
          ⏱️ {nodeData.estimatedTime}
        </div>
      )}
    </div>
  );
});
ActionNode.displayName = 'ActionNode';

export const AutomationNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as RunbookNodeData;
  const colors = RUNBOOK_COLORS.automation;

  return (
    <div
      className={`px-3 py-2 min-w-[160px] rounded-lg border-2 border-dashed shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-green-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-green-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-green-500" />

      <div className="flex items-center gap-2">
        <RobotIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
      <div className="text-xs mt-1 opacity-70" style={{ color: colors.text }}>
        Automated
      </div>
    </div>
  );
});
AutomationNode.displayName = 'AutomationNode';

export const EscalationNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as RunbookNodeData;
  const colors = RUNBOOK_COLORS.escalation;

  return (
    <div
      className={`px-3 py-2 min-w-[140px] rounded-lg border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-purple-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-purple-500" />
      <Handle type="source" position={Position.Bottom} className="!bg-purple-500" />

      <div className="font-semibold text-sm flex items-center gap-2" style={{ color: colors.text }}>
        <span>📞</span>
        {nodeData.label}
      </div>
    </div>
  );
});
EscalationNode.displayName = 'EscalationNode';

export const ResolutionNode = memo(({ data, selected }: NodeProps) => {
  const nodeData = data as RunbookNodeData;
  const colors = RUNBOOK_COLORS.resolution;

  return (
    <div
      className={`px-3 py-2 min-w-[150px] rounded-full border-2 shadow-md transition-all duration-200 ${
        selected ? 'ring-2 ring-green-400 ring-offset-2' : ''
      }`}
      style={{ backgroundColor: colors.bg, borderColor: colors.border }}
    >
      <Handle type="target" position={Position.Top} className="!bg-green-600" />

      <div className="flex items-center gap-2 justify-center">
        <CheckIcon />
        <span className="font-semibold text-sm" style={{ color: colors.text }}>
          {nodeData.label}
        </span>
      </div>
    </div>
  );
});
ResolutionNode.displayName = 'ResolutionNode';

// ============= Node Type Registry =============

export const phase3NodeTypes = {
  // C4/System Context
  person: PersonNode,
  softwareSystem: SoftwareSystemNode,
  externalSystem: ExternalSystemNode,

  // Security/Threat Model
  threat: ThreatNode,
  control: ControlNode,
  trustBoundary: TrustBoundaryNode,

  // Service Mesh
  meshService: MeshServiceNode,
  gateway: GatewayNode,
  loadBalancer: LoadBalancerNode,

  // SRE Runbook
  trigger: TriggerNode,
  decision: DecisionNode,
  action: ActionNode,
  automation: AutomationNode,
  escalation: EscalationNode,
  resolution: ResolutionNode,
};
